import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, createStyles, Grid, IconButton, Theme, Typography, WithStyles, withStyles } from "@material-ui/core";
import ReceiptIcon from "@material-ui/icons/Receipt";
import withConfirm, { IConfirmContext } from "../../../Common/dialog/Confirm";
import EnhancedTable from "../../../Common/EnhancedTable/EnhancedTable";
import { ApiBackend } from "../../../../providers/apibackend";
import { CompanyReservation } from "../../../../model/CompanyReservation";
import { IColumnDefinition } from "../../../Common/EnhancedTable/models";
import VasaloppetTooltip from "../../../Common/VasaloppetTooltip";
import { Edit } from "@material-ui/icons";
import ManageOrder from "../../../Orders/ManageOrder";
import CreateReservationDialog from "./CreateReservationDialog";
import EditReservationDialog from "./EditReservationDialog";

type EditB2bCompanyReservationsProps = {
    companyId: string;
    companyName: string;
}

const EditB2bCompanyReservations = (props: EditB2bCompanyReservationsProps & WithStyles & IConfirmContext) => {
    const [loading, setLoading] = useState(false);
    const [reservations, setReservations] = useState<CompanyReservation[]>([]);
    const [showCreateReservation, setShowCreateReservation] = useState(false);
    const [editReservation, setEditReservation] = useState<CompanyReservation | null>(null);
    const [editOrderId, setEditOrderId] = useState<string | null>(null);

    const api = new ApiBackend();

    useEffect(() => {
        performSearch();
    }, []);

    const columnDefinitions: IColumnDefinition<CompanyReservation>[] = [
        {
            propName: "orderPublicId",
            label: "OrderID",
            renderCell: (row) => {
                return (
                    <Typography style={{ textDecoration: "underline", cursor: "pointer" }}
                        onClick={() => {
                            setEditOrderId(row.orderId);
                        }}>
                        {row.orderPublicId}
                    </Typography>
                );
            }
        },
        { propName: "productName", label: "Produkt" },
        { propName: "productVariantName", label: "Variant" },
        { propName: "reservationName", label: "Reservationsnamn" },
        { propName: "total", label: "Totalt antal" },
        { propName: "numUsed", label: "Nyttjade" },
        {
            renderCell: (row) => {
                return (<>
                    <IconButton color="default" size="small"
                        onClick={async () => {
                            setEditOrderId(row.orderId);
                        }}
                    >
                        <VasaloppetTooltip title="Visa order">
                            <ReceiptIcon fontSize="medium" />
                        </VasaloppetTooltip>

                    </IconButton>
                    <IconButton color="default" size="small"
                        onClick={() => {
                            setEditReservation(row);
                        }}
                    >
                        <VasaloppetTooltip title="Redigera reservation">
                            <Edit fontSize="medium" />
                        </VasaloppetTooltip>
                    </IconButton>
                </>);
            }
        },
    ];

    const render = () => {
        const { companyId, companyName, classes } = props;

        const defaultBoxStyle = {
            display: "flex",
            flexDirection: "row",
        };

        return (<>
            <Card>
                <CardHeader
                    className={classes.cardHeader}
                    title={`Reservationer för ${companyName}`}
                    action={
                        <Box {...defaultBoxStyle} style={{ gap: "16px", marginLeft: "auto", marginTop: 8, marginRight: 8 }}>
                            <Button color="primary" variant="text"
                                disabled={loading}
                                onClick={() => setShowCreateReservation(true)}
                            >
                                Skapa ny reservation
                            </Button>
                        </Box>
                    }
                />

                <CardContent>
                    <Grid container className={classes.root} spacing={2}>
                        <Grid item xs={12}>
                            <EnhancedTable<CompanyReservation>
                                columnDefinitions={columnDefinitions}
                                data={reservations ?? []}
                                noPagination
                                noSort
                                loading={loading}
                                dense
                                sticky="right"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {showCreateReservation &&
                <CreateReservationDialog companyId={companyId}
                    onAbort={() => {
                        setShowCreateReservation(false);
                    }}
                    onSave={async () => {
                        setShowCreateReservation(false);
                        await performSearch();
                    }}
                />
            }
            {editReservation &&
                <EditReservationDialog companyId={companyId} reservation={editReservation}
                    onAbort={() => {
                        setEditReservation(null);
                    }}
                    onSave={async () => {
                        setEditReservation(null);
                        await performSearch();
                    }}
                />
            }
            {editOrderId &&
                <ManageOrder orderId={editOrderId}
                    close={async () => {
                        setEditOrderId(null);
                        await performSearch();
                    }}
                />
            }
        </>);
    };

    const performSearch = async () => {
        const { companyId } = props;

        setLoading(true);

        try {
            const res = await api.listCompanyReservations(companyId);
            setReservations(res.reservations);
        } finally {
            setLoading(false);
        }
    };

    return render();
};

const useStyles = ({ palette }: Theme) => createStyles({
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    }
});

export default withStyles(useStyles)(withConfirm(EditB2bCompanyReservations));